import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  FiClock,
  FiSearch,
  FiX,
  FiSend,
  FiRefreshCw,
  FiXCircle,
  FiCheckCircle,
  FiAlertCircle,
  FiMail,
  FiUser,
  FiZap,
  FiCalendar,
  FiArrowRight,
  FiTrash2,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CardSkeleton } from "@/components/common";
import { getCronJob, nextRunMs, formatCountdown } from "./cron-schedule";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";

type ScheduledEmail = {
  id: string;
  automationId: string;
  customerId: string;
  scheduledFor: string;
  status: "pending" | "sending" | "sent" | "failed" | "cancelled" | "skipped";
  sentAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  errorMessage: string | null;
  retryCount: number;
  createdAt: string;
  customer: {
    id: string;
    name: string | null;
    email: string;
  } | null;
  automation: {
    id: string;
    name: string;
    triggerType: string;
  } | null;
  countdown: string;
};

type Stats = {
  pending: number;
  sentToday: number;
  sentThisWeek: number;
  sentThisMonth: number;
  failedThisWeek: number;
  openRate: number;
  clickRate: number;
  byAutomation: Array<{
    id: string;
    name: string;
    pending: number;
    sent: number;
  }>;
};

const statusConfig: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: { label: "Pending", color: "bg-blue-100 text-blue-700", icon: FiClock },
  sending: { label: "Sending", color: "bg-yellow-100 text-yellow-700", icon: FiSend },
  sent: { label: "Sent", color: "bg-green-100 text-green-700", icon: FiCheckCircle },
  failed: { label: "Failed", color: "bg-red-100 text-red-700", icon: FiAlertCircle },
  cancelled: { label: "Cancelled", color: "bg-gray-100 text-gray-700", icon: FiXCircle },
  skipped: { label: "Skipped", color: "bg-gray-100 text-gray-600", icon: FiX },
};

export function ScheduledEmailsPage() {
  const [emails, setEmails] = useState<ScheduledEmail[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isProcessing, setIsProcessing] = useState(false);
  const [sendNowModalOpen, setSendNowModalOpen] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<ScheduledEmail | null>(null);
  const [isSendingNow, setIsSendingNow] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEmails, setTotalEmails] = useState(0);
  const EMAILS_PER_PAGE = 20;
  const [now, setNow] = useState(() => Date.now());

  // Tick every second for countdowns
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  // Cron schedules come from the shared cron-schedule module — single source
  // of truth so this page stays aligned with workers/cron-jobs/wrangler.toml.
  const processAutomationsJob = useMemo(() => getCronJob("process-automations"), []);
  const sendEmailsJob = useMemo(() => getCronJob("send-scheduled-emails"), []);
  const scrapeRatesJob = useMemo(() => getCronJob("scrape-rba-rates"), []);
  const nextProcessAutomations = useMemo(
    () => nextRunMs(processAutomationsJob.cadence, now),
    [processAutomationsJob, now],
  );
  const nextSendEmails = useMemo(
    () => nextRunMs(sendEmailsJob.cadence, now),
    [sendEmailsJob, now],
  );
  const nextScrapeRates = useMemo(
    () => nextRunMs(scrapeRatesJob.cadence, now),
    [scrapeRatesJob, now],
  );

  const fetchEmails = useCallback(async () => {
    try {
      const params = new URLSearchParams({
        limit: String(EMAILS_PER_PAGE),
        offset: String((currentPage - 1) * EMAILS_PER_PAGE),
      });
      if (statusFilter !== "all") {
        params.set("status", statusFilter);
      }
      if (searchQuery.trim()) {
        params.set("search", searchQuery.trim());
      }

      const response = await fetch(`/api/admin/scheduled-emails?${params}`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json() as { emails: ScheduledEmail[]; total: number };
        setEmails(data.emails);
        setTotalEmails(data.total);
      }
    } catch (error) {
      console.error("Failed to fetch scheduled emails:", error);
    } finally {
      setIsLoading(false);
    }
  }, [statusFilter, searchQuery, currentPage]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch("/api/admin/scheduled-emails/stats", {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json() as Stats;
        setStats(data);
      }
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchEmails();
    fetchStats();
  }, [fetchEmails, fetchStats]);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(totalEmails / EMAILS_PER_PAGE));
  const safePage = Math.min(currentPage, totalPages);

  const handleCancel = async (emailId: string) => {
    try {
      const response = await fetch(`/api/admin/scheduled-emails/${emailId}/cancel`, {
        method: "PATCH",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Email cancelled");
        fetchEmails();
        fetchStats();
      } else {
        toast.error("Failed to cancel email");
      }
    } catch (error) {
      toast.error("Failed to cancel email");
    }
  };

  const handleDelete = async (emailId: string) => {
    try {
      const response = await fetch(`/api/admin/scheduled-emails/${emailId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Email removed");
        fetchEmails();
        fetchStats();
      } else {
        toast.error("Failed to delete email");
      }
    } catch {
      toast.error("Failed to delete email");
    }
  };

  const handleRetry = async (emailId: string) => {
    try {
      const response = await fetch(`/api/admin/scheduled-emails/${emailId}/retry`, {
        method: "POST",
        credentials: "include",
      });

      if (response.ok) {
        toast.success("Email queued for retry");
        fetchEmails();
        fetchStats();
      } else {
        toast.error("Failed to retry email");
      }
    } catch (error) {
      toast.error("Failed to retry email");
    }
  };

  const handleProcessNow = async () => {
    setIsProcessing(true);
    try {
      // First process automations
      await fetch("/api/cron/process-automations", {
        method: "POST",
        credentials: "include",
      });

      // Then send pending emails
      const response = await fetch("/api/cron/send-scheduled-emails", {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json() as { message?: string; data?: { sent: number; failed: number } };

      if (response.ok) {
        toast.success(data.message || "Processed scheduled emails");
        fetchEmails();
        fetchStats();
      } else {
        toast.error("Failed to process emails");
      }
    } catch (error) {
      toast.error("Failed to process emails");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleSendNow = async () => {
    if (!selectedEmail) return;

    setIsSendingNow(true);
    try {
      const response = await fetch(`/api/admin/scheduled-emails/${selectedEmail.id}/send-now`, {
        method: "POST",
        credentials: "include",
      });

      const data = await response.json() as { message?: string; error?: string };

      if (response.ok) {
        toast.success(data.message || "Email sent successfully");
        setSendNowModalOpen(false);
        setSelectedEmail(null);
        fetchEmails();
        fetchStats();
      } else {
        toast.error(data.error || "Failed to send email");
      }
    } catch (error) {
      toast.error("Failed to send email");
    } finally {
      setIsSendingNow(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-medium text-slate-900">
            Scheduled Emails
          </h1>
          <p className="text-muted-foreground mt-1">
            View and manage pending automated emails
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild>
            <Link to="/admin/automations">
              <FiZap className="w-4 h-4 mr-2" />
              Automations
            </Link>
          </Button>
          <Button onClick={handleProcessNow} disabled={isProcessing}>
            <FiRefreshCw className={`w-4 h-4 mr-2 ${isProcessing ? "animate-spin" : ""}`} />
            {isProcessing ? "Processing..." : "Process Now"}
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <FiClock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.pending}</p>
                  <p className="text-xs text-muted-foreground">Pending</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <FiSend className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.sentThisWeek}</p>
                  <p className="text-xs text-muted-foreground">Sent This Week</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <FiAlertCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.failedThisWeek}</p>
                  <p className="text-xs text-muted-foreground">Failed</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Cron Job Countdowns */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FiRefreshCw className="w-4 h-4" />
            Automated Background Jobs
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <div className="p-2 bg-blue-100 rounded-lg shrink-0">
                <FiZap className="w-4 h-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{processAutomationsJob.label}</p>
                <p className="text-xs text-muted-foreground">{processAutomationsJob.description}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold tabular-nums text-blue-700">{formatCountdown(nextProcessAutomations, now)}</p>
                <p className="text-[10px] text-muted-foreground">{processAutomationsJob.cadenceLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <div className="p-2 bg-green-100 rounded-lg shrink-0">
                <FiSend className="w-4 h-4 text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{sendEmailsJob.label}</p>
                <p className="text-xs text-muted-foreground">{sendEmailsJob.description}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold tabular-nums text-green-700">{formatCountdown(nextSendEmails, now)}</p>
                <p className="text-[10px] text-muted-foreground">{sendEmailsJob.cadenceLabel}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
              <div className="p-2 bg-amber-100 rounded-lg shrink-0">
                <FiCalendar className="w-4 h-4 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium">{scrapeRatesJob.label}</p>
                <p className="text-xs text-muted-foreground">{scrapeRatesJob.description}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold tabular-nums text-amber-700">{formatCountdown(nextScrapeRates, now)}</p>
                <p className="text-[10px] text-muted-foreground">{scrapeRatesJob.cadenceLabel}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* By Automation Breakdown */}
      {stats && stats.byAutomation.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">By Automation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              {stats.byAutomation.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-3 px-4 py-2 bg-muted rounded-lg"
                >
                  <span className="font-medium">{item.name}</span>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    {item.pending} pending
                  </Badge>
                  <Badge variant="secondary" className="bg-green-100 text-green-700">
                    {item.sent} sent
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground w-4 h-4" />
          <Input
            placeholder="Search by customer or automation..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <FiX className="w-4 h-4" />
            </button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Emails List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FiMail className="w-5 h-5" />
            Scheduled Emails ({totalEmails})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-3">
              <CardSkeleton />
              <CardSkeleton />
              <CardSkeleton />
            </div>
          ) : emails.length === 0 ? (
            <div className="text-center py-12">
              <FiClock className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
              <p className="text-muted-foreground mb-4">
                {searchQuery || statusFilter !== "all"
                  ? "No emails found"
                  : "No scheduled emails yet"}
              </p>
              <p className="text-sm text-muted-foreground">
                Emails are scheduled automatically based on your{" "}
                <Link to="/admin/automations" className="text-primary underline">
                  automations
                </Link>
              </p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
              {emails.map((email) => {
                const config = statusConfig[email.status] || statusConfig.pending;
                const StatusIcon = config.icon;
                const isPending = email.status === "pending";
                const isFailed = email.status === "failed";

                return (
                  <div
                    key={email.id}
                    className="flex flex-col lg:flex-row lg:items-center gap-4 p-4 rounded-lg bg-muted/50 hover:bg-muted/70 transition-colors"
                  >
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-3">
                        <div className={`p-2 rounded-lg shrink-0 ${config.color}`}>
                          <StatusIcon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="flex items-center gap-1 font-medium">
                              <FiUser className="w-3 h-3" />
                              {email.customer?.name || "Unknown"}
                            </span>
                            <FiArrowRight className="w-3 h-3 text-muted-foreground" />
                            <span className="text-sm text-muted-foreground">
                              {email.customer?.email}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <FiZap className="w-3 h-3" />
                              {email.automation?.name || "Unknown automation"}
                            </span>
                            <span className="flex items-center gap-1">
                              <FiCalendar className="w-3 h-3" />
                              {new Date(email.scheduledFor).toLocaleString()}
                            </span>
                          </div>
                          {email.errorMessage && (
                            <p className="text-xs text-red-600 mt-1">
                              Error: {email.errorMessage}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Countdown / Status */}
                    <div className="flex items-center gap-4">
                      {isPending && (
                        <div className="text-center px-4 py-2 bg-blue-50 rounded-lg">
                          <p className="text-lg font-bold text-blue-700">{email.countdown}</p>
                          <p className="text-xs text-blue-600">until send</p>
                        </div>
                      )}

                      {(email.status === "cancelled" || email.status === "skipped") && (() => {
                        const scheduledDate = new Date(email.scheduledFor).getTime();
                        const expiresAt = scheduledDate + 7 * 24 * 60 * 60 * 1000;
                        const daysLeft = Math.max(0, Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24)));
                        return (
                          <div className="text-center px-3 py-1.5 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-xs text-muted-foreground">
                              Auto-removes in <span className="font-medium">{daysLeft}d</span>
                            </p>
                          </div>
                        );
                      })()}

                      <Badge variant="secondary" className={config.color}>
                        {config.label}
                      </Badge>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 shrink-0">
                      {isPending && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedEmail(email);
                              setSendNowModalOpen(true);
                            }}
                            title="Send Now"
                          >
                            <FiSend className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCancel(email.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            title="Cancel"
                          >
                            <FiXCircle className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      {isFailed && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRetry(email.id)}
                          title="Retry"
                        >
                          <FiRefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                      {(email.status === "cancelled" || email.status === "skipped" || email.status === "failed") && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(email.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>

        {/* Pagination */}
        {totalEmails > EMAILS_PER_PAGE && (
          <div className="flex items-center justify-between px-6 py-3 border-t">
            <p className="text-sm text-muted-foreground">
              Showing {((safePage - 1) * EMAILS_PER_PAGE) + 1}–{Math.min(safePage * EMAILS_PER_PAGE, totalEmails)} of {totalEmails}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="h-8 w-8 p-0"
              >
                <FiChevronLeft className="w-4 h-4" />
              </Button>
              <span className="text-sm font-medium px-3">
                Page {safePage} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="h-8 w-8 p-0"
              >
                <FiChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Send Now Confirmation Dialog */}
      <Dialog open={sendNowModalOpen} onOpenChange={setSendNowModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Email Now?</DialogTitle>
            <DialogDescription>
              This will send the email immediately, bypassing the scheduled time.
            </DialogDescription>
          </DialogHeader>
          {selectedEmail && (
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <p className="font-medium">{selectedEmail.automation?.name}</p>
              <p className="text-sm text-muted-foreground">
                To: {selectedEmail.customer?.name || "Unknown"} ({selectedEmail.customer?.email})
              </p>
              <p className="text-sm text-muted-foreground">
                Originally scheduled: {new Date(selectedEmail.scheduledFor).toLocaleString()}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendNowModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendNow} disabled={isSendingNow}>
              <FiSend className="w-4 h-4 mr-2" />
              {isSendingNow ? "Sending..." : "Send Now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
